import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  CloudDownload as CloudDownloadIcon,
  Summarize as SummarizeIcon,
  Description as TranscriptIcon,
  Headphones as AudioIcon
} from '@mui/icons-material';
import { supabase } from '../../lib/supabaseClient';
import { getAuthUser } from '../../services/authService';
import { useEntity } from '../../context/EntityContext';
import { fetchCallAudio, triggerCallStatusPoll } from '../../services/elevenLabsService';

interface CallLog {
  id: string;
  conversation_id: string;
  application_id: string;
  agent_id: string;
  status: string;
  phone_number: string;
  start_time_unix_secs: number;
  call_duration_secs: number;
  cost: number;
  audio_url: string;
  transcript: any[];
  transcript_summary: string | null;
  metadata: any;
  created_at: string;
  // Joined data
  applicant_name?: string;
  job_title?: string;
}

export const CallLogsPage: React.FC = () => {
  const theme = useTheme();
  const { entity } = useEntity();
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [audioElements] = useState<Map<string, HTMLAudioElement>>(new Map());
  const [selectedCall, setSelectedCall] = useState<CallLog | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsTab, setDetailsTab] = useState(0);
  const [fetchingAudio, setFetchingAudio] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string[]>([]);

  useEffect(() => {
    loadCallLogs();

    return () => {
      // Cleanup audio elements
      audioElements.forEach(audio => {
        audio.pause();
        audio.src = '';
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.id]);

  const loadCallLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const user = await getAuthUser();
      if (!user) {
        setError('You must be logged in');
        return;
      }

      // Fetch call logs with application data
      const { data, error: fetchError } = await supabase
        .from('call_logs')
        .select(`
          *,
          application:applications!call_logs_application_id_fkey(
            applicant_name,
            job:jobs!applications_job_id_fkey(title)
          )
        `)
        .eq('entity_id', user.entityId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      const formattedLogs = (data || []).map((log: any) => ({
        ...log,
        applicant_name: log.application?.applicant_name,
        job_title: log.application?.job?.title,
      }));

      setCallLogs(formattedLogs);

      // Auto-fetch audio for ended calls that don't have audio yet
      const endedCallsWithoutAudio = formattedLogs.filter(
        (log: CallLog) => ['ended', 'done', 'completed'].includes(log.status.toLowerCase()) && !log.audio_url
      );
      if (endedCallsWithoutAudio.length > 0) {
        console.log(`Auto-fetching audio for ${endedCallsWithoutAudio.length} ended calls...`);
        endedCallsWithoutAudio.forEach((call: CallLog) => {
          autoFetchAudio(call);
        });
      }

      // Check for initiated calls and update their status
      const initiatedCalls = formattedLogs.filter(
        (log: CallLog) => log.status.toLowerCase() === 'initiated'
      );

      if (initiatedCalls.length > 0) {
        console.log(`Found ${initiatedCalls.length} initiated calls, checking status...`);
        // Update status for initiated calls in the background
        initiatedCalls.forEach((call: CallLog) => {
          checkAndUpdateCallStatus(call);
        });
      }
    } catch (err: any) {
      console.error('Error loading call logs:', err);
      setError('Failed to load call logs');
    } finally {
      setLoading(false);
    }
  };

  const autoFetchAudio = async (call: CallLog) => {
    try {
      console.log('Auto-fetching audio for call:', call.id);
      const result = await fetchCallAudio(call.conversation_id, call.id);
      if (result.success) {
        console.log('Auto-fetch audio succeeded for:', call.id);
        await refreshCallLog(call.id);
      }
    } catch (err: any) {
      // Silently handle errors - user can still use manual fetch button
      console.warn('Auto-fetch audio failed for:', call.id, err.message);
    }
  };

  const checkAndUpdateCallStatus = async (call: CallLog) => {
    try {
      setUpdatingStatus(prev => [...prev, call.id]);
      console.log('Checking status for conversation:', call.conversation_id);

      const result = await triggerCallStatusPoll(call.conversation_id);

      if (result.success) {
        console.log('Status updated successfully for:', call.conversation_id);
        // Refresh only this specific call log without triggering another status check
        await refreshCallLog(call.id);

        // After refresh, check if the call ended but has no audio - auto-fetch it
        const { data: refreshedCall } = await supabase
          .from('call_logs')
          .select('status, audio_url, conversation_id')
          .eq('id', call.id)
          .single();

        if (refreshedCall && ['ended', 'done', 'completed'].includes(refreshedCall.status?.toLowerCase()) && !refreshedCall.audio_url) {
          autoFetchAudio({ ...call, status: refreshedCall.status });
        }
      } else {
        console.error('Failed to update status:', result.error);
      }
    } catch (err: any) {
      console.error('Error checking call status:', err);
    } finally {
      setUpdatingStatus(prev => prev.filter(id => id !== call.id));
    }
  };

  const refreshCallLog = async (callId: string) => {
    try {
      const user = await getAuthUser();
      if (!user) return;

      const { data, error: fetchError } = await supabase
        .from('call_logs')
        .select(`
          *,
          application:applications!call_logs_application_id_fkey(
            applicant_name,
            job:jobs!applications_job_id_fkey(title)
          )
        `)
        .eq('id', callId)
        .single();

      if (fetchError) throw fetchError;

      if (data) {
        const updatedLog = {
          ...data,
          applicant_name: data.application?.applicant_name,
          job_title: data.application?.job?.title,
        };

        // Update the specific call log in state
        setCallLogs(prev =>
          prev.map(log => (log.id === callId ? updatedLog : log))
        );
      }
    } catch (err: any) {
      console.error('Error refreshing call log:', err);
    }
  };

  const handlePlayPause = (callId: string, audioUrl: string) => {
    // Pause any currently playing audio
    if (playingAudio && playingAudio !== callId) {
      const currentAudio = audioElements.get(playingAudio);
      if (currentAudio) {
        currentAudio.pause();
      }
    }

    // Get or create audio element
    let audio = audioElements.get(callId);
    if (!audio) {
      audio = new Audio(audioUrl);
      audio.addEventListener('ended', () => {
        setPlayingAudio(null);
      });
      audio.addEventListener('error', (e) => {
        console.error('Audio error:', e);
        setError('Failed to load audio file');
        setPlayingAudio(null);
      });
      audioElements.set(callId, audio);
    }

    // Toggle play/pause
    if (playingAudio === callId) {
      audio.pause();
      setPlayingAudio(null);
    } else {
      audio.play();
      setPlayingAudio(callId);
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'ended':
      case 'done':
        return 'success';
      case 'initiated':
        return 'info';
      case 'in_progress':
      case 'in-progress':
        return 'warning';
      case 'failed':
      case 'not_answered':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'not_answered':
        return 'Not Answered';
      case 'in_progress':
      case 'in-progress':
        return 'In Progress';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const handleViewDetails = (call: CallLog) => {
    setSelectedCall(call);
    setDetailsTab(0); // Reset to Summary tab
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedCall(null);
    setDetailsTab(0);
  };

  const handleDetailsTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setDetailsTab(newValue);
  };

  const handleFetchAudio = async () => {
    if (!selectedCall) return;

    setFetchingAudio(true);
    setError(null);

    console.log('=== Fetching Audio ===');
    console.log('Conversation ID:', selectedCall.conversation_id);
    console.log('Call ID:', selectedCall.id);
    console.log('Call Status:', selectedCall.status);
    console.log('Full Call Data:', selectedCall);

    try {
      const result = await fetchCallAudio(selectedCall.conversation_id, selectedCall.id);

      if (result.success && result.audioUrl) {
        // Update the selected call with the new audio URL
        setSelectedCall({
          ...selectedCall,
          audio_url: result.audioUrl,
        });

        // Reload call logs to update the table
        await loadCallLogs();

        setError(null);
      } else {
        setError(result.error || 'Failed to fetch audio');
      }
    } catch (err: any) {
      console.error('Error fetching audio:', err);
      setError('Failed to fetch audio');
    } finally {
      setFetchingAudio(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          p: 4,
          mb: 4
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              bgcolor: theme.palette.primary.main,
              width: 56,
              height: 56
            }}
          >
            <PhoneIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h3" fontWeight={700}>
              AI Call Recordings
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Listen to recorded candidate screening calls
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={3} sx={{ mt: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} color="primary.main">
              {callLogs.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Calls
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={700} color="success.main">
              {callLogs.filter(log => log.audio_url).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              With Recordings
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Call Logs Table */}
      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          {callLogs.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 8 }}>
              <PhoneIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                No Call Recordings Yet
              </Typography>
              <Typography variant="body1" color="text.secondary">
                AI call recordings will appear here after calls are completed
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell sx={{ fontWeight: 700 }}>Candidate</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Job</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Recording</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {callLogs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon fontSize="small" color="action" />
                        <Typography fontWeight={600}>
                          {log.applicant_name || 'Unknown'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {log.job_title || 'Unknown Position'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={getStatusLabel(log.status)}
                          size="small"
                          color={getStatusColor(log.status) as any}
                        />
                        {updatingStatus.includes(log.id) && (
                          <Tooltip title="Updating status...">
                            <CircularProgress size={16} />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TimeIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {log.start_time_unix_secs
                            ? formatDate(log.start_time_unix_secs)
                            : new Date(log.created_at).toLocaleString()}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {log.call_duration_secs
                          ? formatDuration(log.call_duration_secs)
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {log.audio_url ? (
                        <Tooltip title={playingAudio === log.id ? 'Pause' : 'Play Recording'}>
                          <IconButton
                            color="primary"
                            onClick={() => handlePlayPause(log.id, log.audio_url)}
                          >
                            {playingAudio === log.id ? <PauseIcon /> : <PlayIcon />}
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No audio
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details & Transcript">
                        <IconButton
                          color="primary"
                          onClick={() => handleViewDetails(log)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Call Details
              </Typography>
              {selectedCall && (
                <Typography variant="body2" color="text.secondary">
                  {selectedCall.applicant_name || 'Unknown'} • {selectedCall.job_title || 'Unknown Position'}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleCloseDetails} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={detailsTab} onChange={handleDetailsTabChange}>
            <Tab icon={<SummarizeIcon />} iconPosition="start" label="Summary" />
            <Tab icon={<TranscriptIcon />} iconPosition="start" label="Transcript" />
            <Tab icon={<AudioIcon />} iconPosition="start" label="Audio" />
          </Tabs>
        </Box>

        <DialogContent>
          {selectedCall && (
            <>
              {/* Summary Tab */}
              {detailsTab === 0 && (
                <Stack spacing={3}>
                  {/* Call Information */}
                  <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      CALL INFORMATION
                    </Typography>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Candidate:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {selectedCall.applicant_name || 'Unknown'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Job Position:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {selectedCall.job_title || 'Unknown Position'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Phone Number:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {selectedCall.phone_number || 'N/A'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Status:</Typography>
                        <Chip
                          label={getStatusLabel(selectedCall.status)}
                          size="small"
                          color={getStatusColor(selectedCall.status) as any}
                        />
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Duration:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {selectedCall.call_duration_secs
                            ? formatDuration(selectedCall.call_duration_secs)
                            : 'N/A'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Date:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {selectedCall.start_time_unix_secs
                            ? formatDate(selectedCall.start_time_unix_secs)
                            : new Date(selectedCall.created_at).toLocaleString()}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>

                  {/* AI Summary */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      AI CALL SUMMARY
                    </Typography>
                    {selectedCall.transcript_summary ? (
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.05),
                          p: 3,
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                        }}
                      >
                        <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                          {selectedCall.transcript_summary}
                        </Typography>
                      </Paper>
                    ) : (
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: alpha(theme.palette.grey[500], 0.05),
                          p: 4,
                          borderRadius: 2,
                          textAlign: 'center'
                        }}
                      >
                        <SummarizeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          No AI summary available for this call yet.
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Summary is generated after the call is completed.
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </Stack>
              )}

              {/* Transcript Tab */}
              {detailsTab === 1 && (
                <Box>
                  {selectedCall.transcript && selectedCall.transcript.length > 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        maxHeight: 500,
                        overflowY: 'auto',
                        bgcolor: alpha(theme.palette.grey[500], 0.05),
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.grey[500], 0.1)}`
                      }}
                    >
                      <Stack spacing={1.5}>
                        {selectedCall.transcript.map((message: any, index: number) => {
                          const isAgent = message.role === 'agent';
                          return (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                justifyContent: isAgent ? 'flex-end' : 'flex-start',
                              }}
                            >
                              <Box
                                sx={{
                                  maxWidth: '75%',
                                  bgcolor: isAgent
                                    ? alpha(theme.palette.primary.main, 0.1)
                                    : alpha(theme.palette.grey[500], 0.08),
                                  p: 1.5,
                                  borderRadius: 2,
                                  ...(isAgent
                                    ? {
                                        borderTopRightRadius: 4,
                                        borderBottomRightRadius: 0,
                                        borderRight: `3px solid ${theme.palette.primary.main}`,
                                      }
                                    : {
                                        borderTopLeftRadius: 4,
                                        borderBottomLeftRadius: 0,
                                        borderLeft: `3px solid ${theme.palette.secondary.main}`,
                                      }),
                                }}
                              >
                                <Typography variant="caption" fontWeight={700} color={isAgent ? 'primary.main' : 'secondary.main'}>
                                  {isAgent ? 'AI Agent' : 'Candidate'}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  {message.message || message.text || message.content || 'No content'}
                                </Typography>
                                {message.timestamp && (
                                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block', textAlign: isAgent ? 'right' : 'left' }}>
                                    {new Date(message.timestamp).toLocaleTimeString()}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Paper>
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: alpha(theme.palette.grey[500], 0.05),
                        p: 6,
                        borderRadius: 2,
                        textAlign: 'center'
                      }}
                    >
                      <TranscriptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No transcript available for this call
                      </Typography>
                    </Paper>
                  )}
                </Box>
              )}

              {/* Audio Tab */}
              {detailsTab === 2 && (
                <Box>
                  {selectedCall.audio_url ? (
                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        p: 3,
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                        AUDIO RECORDING
                      </Typography>
                      <audio controls style={{ width: '100%' }}>
                        <source src={selectedCall.audio_url} type="audio/mpeg" />
                        Your browser does not support the audio element.
                      </audio>
                    </Paper>
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: alpha(theme.palette.grey[500], 0.05),
                        p: 6,
                        borderRadius: 2,
                        textAlign: 'center'
                      }}
                    >
                      <AudioIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        No audio recording available yet
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={fetchingAudio ? <CircularProgress size={20} /> : <CloudDownloadIcon />}
                        onClick={handleFetchAudio}
                        disabled={fetchingAudio}
                        sx={{ mt: 2 }}
                      >
                        {fetchingAudio ? 'Fetching Audio...' : 'Fetch Call Recording'}
                      </Button>
                    </Paper>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDetails} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
